import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cx from 'classnames';
import HeartIcon from 'components/icons/Heart';
import HeartFilled from 'components/icons/HeartFilled';
import useFavorites from 'utils/hooks/useFavorites';
import { AspectRatio } from 'react-aspect-ratio';
// import LazyLoad from "react-lazyload";
import { getPriceDifferential } from 'utils/getPriceDifferential';
import { hasPrice } from 'utils/parsePrice';

const CardContainer = styled.div`
  margin-top: 1.25rem;
  margin-bottom: 0.25rem;
`;

const AvailableFlag = styled.div`
  border-radius: 0.813rem;
  min-height: 1.25rem;
`;

const QMIFlagMessageContainer = styled.div`
  border-radius: 0.813rem;
  width: fit-content;
  border-width: 0.125rem;
`;

const MessageFlagLogo = styled.div`
  width: fit-content;
`;

const CardTitle = styled.div`
  letter-spacing: -0.029rem;
`;

const CardDescription = styled.div.attrs({
  className:
    'font-graphie text-mattamy-blue text-base leading-relaxed md:leading-5 md:mb-0 flex items-start',
})`
  letter-spacing: 0.007rem;
`;

const isZeroValue = (label) => {
  if (label) {
    const labelNumber = label.split(' ')[0];
    if (labelNumber && labelNumber === '0') return true;
    return false;
  }
  return true;
};

const getDefaultContentTextValue = (attributes) => {
  for (let i = 0; i < attributes.length; i++) {
    if (!isZeroValue(attributes[i].label) && attributes[i].icon !== 'stories')
      return false;
  }
  return true;
};

const isStudio = (bedsLabel) => {
  if (bedsLabel && bedsLabel === '0') return true;
  return false;
};

const cleanLabel = (label) => {
  const labelValues = label.split(' ');
  if (labelValues && labelValues.length > 0) {
    const ranges = labelValues[0].split('-');
    if (ranges.length > 1) {
      let newLabel = '';
      if (ranges[0] !== '0') {
        newLabel = newLabel + ranges[0];
      }
      if (ranges[0] !== '0' && ranges[1] !== '0') {
        newLabel = newLabel + '-';
      }
      if (ranges[1] !== '0' && ranges[1] !== '0') {
        newLabel = newLabel + ranges[1];
      }
      if (newLabel) {
        newLabel = newLabel + ' ' + labelValues[1];
        if (labelValues[2]) newLabel = newLabel + ' ' + labelValues[2];
      }
      return newLabel;
    }
  }
  return label;
};

const getLabel = (label) => {
  const newLabel = cleanLabel(label);
  const labelValues = newLabel.split(' ');

  if (labelValues && labelValues.length > 0) {
    const ranges = labelValues[0].split('-');
    if (ranges.length > 1) {
      return ranges[1] > 1 ? newLabel + 's' : newLabel;
    }
    return ranges[0] > 1 ? newLabel + 's' : newLabel;
  }
  return newLabel;
};

const QMICardMobile = (props) => {
  const {
    id,
    type,
    image,
    title,
    planName,
    homeType,
    attributes,
    price,
    availableDateMessage,
    modelHome,
    country,
    url,
    usContentText = 'Floorplan details coming soon',
    caContentText = 'Floorplan details coming soon',
    showNewSchemaColor,
    newSchemaColorHexaCode,
    index,
    mobileVisibleCards,
    messageFlag,
    rvGarage,
    priceDifferentialText,
    favoritesList,
    messageFlagLogo,
    messageFlagLogoAlt,
  } = props;
  const imageSrc = (image && image.srcMobile) || (image && image.src);
  const beds = attributes && attributes.find((e) => e.icon === 'bed');
  const baths = attributes && attributes.find((e) => e.icon === 'bath');
  const halfBaths =
    attributes && attributes.find((e) => e.icon === 'half-baths');
  const sqft = attributes && attributes.find((e) => e.icon === 'ruler');
  const garage = attributes && attributes.find((e) => e.icon === 'car');
  const hasAvailableFlag =
    availableDateMessage || (type === 'Plan' && modelHome);
  const hasCardFlags = hasAvailableFlag || messageFlag || messageFlagLogo;
  const hasDisplayPrice = price && hasPrice(price.price);

  const { saveFavorite, deleteFavorite } = useFavorites();
  const isFavorite = favoritesList.includes(id);
  const handleFavoriteClick = (id) => {
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };

  return (
    <CardContainer className={index < mobileVisibleCards ? '' : 'hidden'}>
      {hasCardFlags && (
        <div className='flex flex-row flex-wrap items-center gap-2 mb-2'>
          {hasAvailableFlag && (
            <AvailableFlag
              className={cx(
                'flex items-center justify-center bg-white border-2 py-1 px-3',
                {
                  'border-mattamy-blue-light text-mattamy-blue-light':
                    !showNewSchemaColor,
                  'border-black text-black': showNewSchemaColor,
                },
              )}
            >
              <p className='px-1 text-center font-trade-gothic-20 uppercase text-xs tracking-wide leading-3 mt-1'>
                {type === 'Plan' && modelHome
                  ? modelHome
                  : availableDateMessage}
              </p>
            </AvailableFlag>
          )}

          {messageFlag && !messageFlagLogo && (
            <QMIFlagMessageContainer
              className={cx(
                'flex items-center justify-center py-1 px-3 text-promo-dark-blue bg-aqua border-promo-dark-blue',
              )}
            >
              <p className='px-1 text-center font-trade-gothic-20 uppercase text-xs tracking-wide leading-3 mt-1'>
                {messageFlag}
              </p>
            </QMIFlagMessageContainer>
          )}

          {messageFlagLogo && (
            <MessageFlagLogo
              className='flex items-center justify-center h-6 bg-white'
              aria-hidden='true'
            >
              <img
                className='object-cover h-full'
                src={messageFlagLogo}
                alt={messageFlagLogoAlt}
              />
            </MessageFlagLogo>
          )}
        </div>
      )}
      <div className='w-full bg-mattamy-white flex flex-col lg:flex-row justify-center items-start md:cursor-pointer'>
        <div className='flex flex-row w-full items-start'>
          <div className='text-center flex flex-col relative rounded overflow-hidden w-3/12'>
            <div className='overflow-hidden rounded'>
              <a href={url} className='flex flex-col w-auto'>
                <AspectRatio ratio={'250/200'} className='w-full'>
                  <div>
                    <img
                      className='flex-1 object-cover h-full min-w-full bg-white'
                      src={imageSrc}
                      alt={(image && image.alt) || ''}
                    />
                  </div>
                </AspectRatio>
              </a>
            </div>
          </div>

          <div className=' ml-4 text-left w-8/12 flex flex-col'>
            <a
              href={url}
              className='flex flex-col w-auto'
              aria-label={
                'View ' +
                title +
                ' information.' +
                (type === 'QMI'
                  ? planName
                    ? `${planName} Floorplan | ${homeType}`
                    : homeType
                  : homeType) +
                ' ' +
                cleanLabel(sqft.label + ' ' + sqft.attribute) +
                ' ' +
                getLabel(beds.label + ' ' + beds.attribute) +
                ' ' +
                getLabel(baths.label + ' ' + baths.attribute) +
                ' ' +
                getLabel(halfBaths.label + ' ' + halfBaths.attribute) +
                ' ' +
                cleanLabel(garage.label + ' ' + garage.attribute) +
                ' ' +
                (hasDisplayPrice
                  ? `${price.normalText} ${price.price}`
                  : price.noPriceText) +
                (price.displayPreviousPrice
                  ? ' Previous price: ' + price.previousPriceQMI
                  : '')
              }
            >
              <CardTitle
                className={cx(
                  'font-trade-gothic-20  text-2xl leading-8 block',
                  {
                    'text-mattamy-blue': !showNewSchemaColor,
                    'text-black': showNewSchemaColor,
                  },
                )}
              >
                {title}
              </CardTitle>
              <span
                className={cx('font-graphie text-xs', {
                  'text-action-blue': !showNewSchemaColor,
                  'text-black': showNewSchemaColor,
                })}
              >
                {type === 'QMI'
                  ? planName
                    ? `${planName} Floorplan | ${homeType}`
                    : homeType
                  : homeType}
              </span>

              <div className='flex flex-row w-full'>
                <div className='w-full flex flex-col justify-end'>
                  <div className='block'>
                    {getDefaultContentTextValue(attributes) &&
                      type === 'Plan' && (
                        <CardDescription className='justify-start   '>
                          <p
                            className={cx(
                              'text-left text-base leading-5 pt-1',
                              {
                                'text-mattamy-blue': !showNewSchemaColor,
                                'text-black': showNewSchemaColor,
                              },
                            )}
                          >
                            {country === 'USA' ? usContentText : caContentText}
                          </p>
                        </CardDescription>
                      )}

                    <CardDescription className='justify-start   '>
                      <p
                        className={cx('break-all mt-1 font-graphie text-xs ', {
                          'text-mattamy-blue': !showNewSchemaColor,
                          'text-black': showNewSchemaColor,
                        })}
                      >
                        {sqft && sqft.label !== '0' && (
                          <>
                            {cleanLabel(sqft.label + ' ' + sqft.attribute)}
                            <span className={'mx-1'}>|</span>
                          </>
                        )}
                        {beds && isStudio(beds.label) && (
                          <>
                            {'Studio'}
                            <span className={'mx-1'}>|</span>
                          </>
                        )}
                        {beds && !isStudio(beds.label) && (
                          <>
                            {getLabel(beds.label + ' ' + beds.attribute)}
                            <span className={'mx-1'}>|</span>
                          </>
                        )}
                        {baths &&
                          baths.label !== '0' &&
                          getLabel(baths.label + ' ' + baths.attribute)}
                      </p>
                    </CardDescription>

                    <CardDescription className='justify-start   '>
                      <p
                        className={cx('break-all mt-1 font-graphie text-xs ', {
                          'text-mattamy-blue': !showNewSchemaColor,
                          'text-black': showNewSchemaColor,
                        })}
                      >
                        {halfBaths && halfBaths.label !== '0' && (
                          <>
                            {getLabel(
                              halfBaths.label + ' ' + halfBaths.attribute,
                            )}
                            <span className={'mx-1'}>|</span>
                          </>
                        )}
                        {garage && garage.label !== '0' && (
                          <>
                            {cleanLabel(
                              rvGarage
                                ? garage.label + ' Car + RV Garage'
                                : garage.label + ' ' + garage.attribute,
                            )}
                          </>
                        )}
                      </p>
                    </CardDescription>

                    <CardDescription
                      className={cx('justify-end flex-col items-end', {
                        'mr-0': !price.displayPreviousPrice,
                        'mr-0': price.displayPreviousPrice,
                      })}
                    >
                      <p
                        className={cx(
                          'break-all mt-1 font-trade-gothic-20 text-xl',
                          {
                            'text-mattamy-blue': !showNewSchemaColor,
                            'text-black': showNewSchemaColor,
                          },
                        )}
                      >
                        {hasDisplayPrice
                          ? `${price.normalText} ${price.price}`
                          : price.noPriceText}
                        {price.displayPreviousPrice && (
                          <span className='ml-2 line-through text-mattamy-gray-light'>
                            {price.previousPriceQMI}
                          </span>
                        )}
                      </p>
                      {price.displayPreviousPrice && (
                        <p>
                          <span className='text-xs leading-2 text-mattamy-gray-light font-graphie ml-2'>
                            {priceDifferentialText}{' '}
                          </span>
                          <span className='text-lg leading-5 font-trade-gothic-20 text-mattamy-gray-light ml-1'>
                            {getPriceDifferential(
                              price.previousPriceQMI,
                              price.price,
                            )}
                          </span>
                        </p>
                      )}
                    </CardDescription>
                  </div>
                </div>
              </div>
            </a>
          </div>

          <div
            className={cx(
              'h-auto text-left w-1/12 flex flex-col items-center ',
              {
                'text-action-blue': !showNewSchemaColor,
                'text-black': showNewSchemaColor,
              },
            )}
            onClick={() => handleFavoriteClick(id)}
            role='button'
            aria-label={isFavorite ? 'Mark as unfavorite' : 'Mark as favorite'}
            aria-pressed={isFavorite ? 'true' : 'false'}
            tabIndex={0}
          >
            {isFavorite ? (
              <HeartFilled className='h-5 w-5' />
            ) : (
              <HeartIcon className='h-5 w-5' />
            )}
          </div>
        </div>
      </div>
    </CardContainer>
  );
};

QMICardMobile.propTypes = {
  props: PropTypes.object,
};

export default QMICardMobile;
